import React from 'react';
import PropTypes from 'prop-types';
import InfoBanner from '@/features/datasets/components/DataCollection/GenerateDataWithAI/shared/InfoBanner';
import TextField from '@/features/datasets/components/DataCollection/GenerateDataWithAI/shared/TextField';
import TextAreaField from '@/features/datasets/components/DataCollection/GenerateDataWithAI/shared/TextAreaField';
import AdvancedSettingsSection, {
    settingsShape,
} from '@/features/datasets/components/DataCollection/GenerateDataWithAI/shared/AdvancedSettingsSection';
import LabelsMultiSelect from '@/features/datasets/components/DataCollection/GenerateDataWithAI/shared/LabelsMultiSelect';
import DatasetNameLoadRow from '@/features/datasets/components/DataCollection/GenerateDataWithAI/shared/DatasetNameLoadRow';
import {
    CUSTOM_GUIDELINES_PLACEHOLDER,
    GENERATION_GOAL_PLACEHOLDER,
    HELP_TEXTS,
    LOAD_PREVIOUS_GENERATION_LABEL,
} from '@/features/datasets/components/DataCollection/GenerateDataWithAI/constants';

/**
 * "Define Task" step body shared by classification, multi-label, NER, and
 * summarization flows: optional dismissible info banner, then the Dataset
 * Name + "Load Previous Generation Configurations" row (when the flow wires
 * `onDatasetNameChange`), then Generation Goal and Description under a
 * "Define Task" heading, with the Active Labels multi-select below them when
 * `labelHeading` is provided (same position as the Retrieval Agent).
 * Flow-specific sections (NER entity tags, multi-label examples) render as
 * children, followed by the shared Advanced settings section when `settings`
 * is provided.
 */
const DefineTaskStep = ({
    bannerText,
    datasetName = '',
    onDatasetNameChange,
    datasetNameError = '',
    sessions = [],
    loadedSessionTitle,
    onLoadSession,
    goal,
    description,
    goalError = false,
    descriptionError = false,
    onGoalChange,
    onDescriptionChange,
    labelHeading,
    labelOptions = [],
    labelNames = [],
    onLabelNamesChange,
    settings,
    onSettingsChange,
    showTextLengthOptionHelp = false,
    children,
    readOnly = false,
}) => {
    const patchSettings = (key) => (value) => onSettingsChange({ ...settings, [key]: value });
    return (
        <>
            {bannerText ? <InfoBanner text={bannerText} /> : null}
            {onDatasetNameChange && !readOnly ? (
                <DatasetNameLoadRow
                    datasetName={datasetName}
                    onDatasetNameChange={onDatasetNameChange}
                    datasetNameError={datasetNameError}
                    loadLabel={LOAD_PREVIOUS_GENERATION_LABEL}
                    sessions={sessions}
                    loadedSessionTitle={loadedSessionTitle}
                    onLoadSession={onLoadSession}
                />
            ) : null}
            <div className="gdai-section">
                <div className="gdai-section-heading fs-unmask">Define Task</div>
                <TextField
                    label="Goal"
                    required
                    help={HELP_TEXTS.generationGoal}
                    placeholder={GENERATION_GOAL_PLACEHOLDER}
                    value={goal}
                    onChange={onGoalChange}
                    error={goalError}
                    readOnly={readOnly}
                />
                <TextAreaField
                    label="Description"
                    required
                    help={HELP_TEXTS.datasetContext}
                    placeholder={CUSTOM_GUIDELINES_PLACEHOLDER}
                    value={description}
                    onChange={onDescriptionChange}
                    height={147}
                    error={descriptionError}
                    readOnly={readOnly}
                />
                {labelHeading ? (
                    <LabelsMultiSelect
                        heading={labelHeading}
                        options={labelOptions}
                        selected={labelNames}
                        onChange={onLabelNamesChange}
                    />
                ) : null}
            </div>
            {children}
            {settings && onSettingsChange ? (
                <AdvancedSettingsSection
                    domain={settings.domain}
                    onDomainChange={patchSettings('domain')}
                    languages={settings.languages}
                    onLanguagesChange={patchSettings('languages')}
                    toneStyles={settings.toneStyles}
                    onToneStylesChange={patchSettings('toneStyles')}
                    textLength={settings.textLength}
                    onTextLengthChange={patchSettings('textLength')}
                    contentType={settings.contentType}
                    onContentTypeChange={patchSettings('contentType')}
                    showTextLengthOptionHelp={showTextLengthOptionHelp}
                />
            ) : null}
        </>
    );
};

DefineTaskStep.propTypes = {
    bannerText: PropTypes.node,
    datasetName: PropTypes.string,
    onDatasetNameChange: PropTypes.func,
    datasetNameError: PropTypes.string,
    sessions: PropTypes.arrayOf(PropTypes.object),
    loadedSessionTitle: PropTypes.string,
    onLoadSession: PropTypes.func,
    goal: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    goalError: PropTypes.bool,
    descriptionError: PropTypes.bool,
    onGoalChange: PropTypes.func.isRequired,
    onDescriptionChange: PropTypes.func.isRequired,
    labelHeading: PropTypes.string,
    labelOptions: PropTypes.arrayOf(PropTypes.string),
    labelNames: PropTypes.arrayOf(PropTypes.string),
    onLabelNamesChange: PropTypes.func,
    settings: settingsShape,
    onSettingsChange: PropTypes.func,
    showTextLengthOptionHelp: PropTypes.bool,
    children: PropTypes.node,
    readOnly: PropTypes.bool,
};

export default DefineTaskStep;
